/**
 * User-controlled preferences for low-quota in-app notifications.
 * Stored locally per browser so the setting works without a round trip.
 */
export interface QuotaNotificationPrefs {
  /** Master switch for low-quota toasts. */
  enabled: boolean;
  /** Warn when remaining scans are at or below this number. */
  threshold: number;
  /** Minimum hours between two low-quota toasts. */
  cooldownHours: number;
}

export const DEFAULT_QUOTA_PREFS: QuotaNotificationPrefs = {
  enabled: true,
  threshold: 1,
  cooldownHours: 6,
};

const KEY = "inquira.quotaNotificationPrefs";

export function loadQuotaPrefs(): QuotaNotificationPrefs {
  if (typeof window === "undefined") return DEFAULT_QUOTA_PREFS;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT_QUOTA_PREFS;
    const parsed = JSON.parse(raw) as Partial<QuotaNotificationPrefs>;
    return {
      enabled: typeof parsed.enabled === "boolean" ? parsed.enabled : DEFAULT_QUOTA_PREFS.enabled,
      threshold: clamp(Number(parsed.threshold), 0, 10, DEFAULT_QUOTA_PREFS.threshold),
      cooldownHours: clamp(Number(parsed.cooldownHours), 0, 48, DEFAULT_QUOTA_PREFS.cooldownHours),
    };
  } catch {
    return DEFAULT_QUOTA_PREFS;
  }
}

export function saveQuotaPrefs(prefs: QuotaNotificationPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
    window.dispatchEvent(new CustomEvent("inquira:quota-prefs-changed"));
  } catch {
    /* storage unavailable — keep in-memory values */
  }
}

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}
